import { useState } from "react";

import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaInfoCircle } from "react-icons/fa";

import { handleColumnToggle } from "@/utils/handleColumnTogle";
import { ResumeIndicatorC1ByHealthTeamData } from "@/services/esus/indicator/esf/c1/getResumeIndicatorC1ByHealthTeam";
import {
	getIndicatorClassification,
	getIndicatorClassificationColorClassName,
	getIndicatorClassificationScores,
	IndicatorEsfEnum,
} from "@/utils/indicatorsEsf";
import { calculatePercentage } from "@/utils/calculatePercentage";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";

type Column = {
	title: string;
	visible: boolean;
};

type IndicatorEsfC1HealthTeamsClassificationTableColumns = {
	healthTeamName: Column;
	totalAttendances: Column;
	totalScheduledAttendances: Column;
	classificationPercentage: Column;
	classification: Column;
};

type IndicatorEsfC1HealthTeamsTableProps = {
	healthTeamsClassifications: ResumeIndicatorC1ByHealthTeamData[];
	isLoading: boolean;
};

const indicatorClassificationScores = getIndicatorClassificationScores(IndicatorEsfEnum.C1);

export function IndicatorEsfC1HealthTeamsClassificationTable({
	healthTeamsClassifications,
	isLoading,
}: Readonly<IndicatorEsfC1HealthTeamsTableProps>) {
	const [columns, setColumns] = useState<IndicatorEsfC1HealthTeamsClassificationTableColumns>({
		healthTeamName: { title: "Equipe", visible: true },
		totalAttendances: { title: "Total de atendimentos", visible: true },
		totalScheduledAttendances: { title: "Programados", visible: true },
		classificationPercentage: { title: "% Classificação", visible: true },
		classification: { title: "Classificação", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th
						className={`text-start px-2 ${!columns.healthTeamName.visible && "d-none"}`}
					>
						{columns.healthTeamName.title}
					</th>
					<th className={`px-2 ${!columns.totalAttendances.visible && "d-none"}`}>
						{columns.totalAttendances.title}
					</th>
					<th
						className={`px-2 ${!columns.totalScheduledAttendances.visible && "d-none"}`}
					>
						{columns.totalScheduledAttendances.title}
					</th>
					<th className={`px-2 ${!columns.classificationPercentage.visible && "d-none"}`}>
						{columns.classificationPercentage.title}
					</th>
					<th className={`px-2 ${!columns.classification.visible && "d-none"}`}>
						{columns.classification.title}{" "}
						<CardOverlayTrigger header={"Classificação"} icon={<FaInfoCircle />}>
							<div className="text-start">
								<div className="text-primary">{`Ótimo: >= ${indicatorClassificationScores.awesome}% e <= ${indicatorClassificationScores.limit}%`}</div>
								<br />
								<div className="text-success">{`Bom: >= ${indicatorClassificationScores.good}% e < ${indicatorClassificationScores.awesome}%`}</div>
								<br />
								<div className="text-warning">
									{`Suficiente: >= ${indicatorClassificationScores.enough}% e < ${indicatorClassificationScores.good}%`}
								</div>
								<br />
								<div className="text-danger">{`Regular: < ${indicatorClassificationScores.enough}%`}</div>
								<br />
							</div>
						</CardOverlayTrigger>
					</th>
					<th className="text-nowrap pb-0">
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEsfC1HealthTeamsClassificationTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEsfC1HealthTeamsClassificationTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEsfC1HealthTeamsClassificationTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && healthTeamsClassifications.length > 0
					? healthTeamsClassifications.map((healthTeamClassification, index) => (
							<tr
								className="text-center"
								key={`health-team-classification-table-row-${index + 1}`}
							>
								<td
									className={`text-start px-2 ${
										!columns.healthTeamName.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>
											{healthTeamClassification.attendanceHealthTeamName ??
												""}
										</div>
										<div className="text-muted">
											{healthTeamClassification.attendanceHealthTeamIne ?? ""}
										</div>
									</div>
								</td>
								<td
									className={`px-2 ${
										!columns.totalAttendances.visible && "d-none"
									}`}
								>
									{healthTeamClassification.totalAttendances ?? 0}
								</td>
								<td
									className={`px-2 ${
										!columns.totalScheduledAttendances.visible && "d-none"
									}`}
								>
									{healthTeamClassification.totalScheduledAttendances ?? 0}
								</td>
								<td
									className={`px-2 ${
										!columns.classificationPercentage.visible && "d-none"
									}`}
								>
									{`${calculatePercentage(
										healthTeamClassification.totalScheduledAttendances,
										healthTeamClassification.totalAttendances
									)}%`}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.classification.visible && "d-none"
									}`}
								>
									<Badge
										bg={getIndicatorClassificationColorClassName(
											IndicatorEsfEnum.C1,
											calculatePercentage(
												healthTeamClassification.totalScheduledAttendances,
												healthTeamClassification.totalAttendances
											)
										)}
									>
										{getIndicatorClassification(
											IndicatorEsfEnum.C1,
											calculatePercentage(
												healthTeamClassification.totalScheduledAttendances,
												healthTeamClassification.totalAttendances
											)
										)}
									</Badge>
								</td>
								<td></td>
							</tr>
					  ))
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) =>
											count + (column?.visible === true ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
